import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { FilterTypeEnum } from "../../services/tank-relationship-data-service/Models";

export enum TankRelationshipTabEnum {
    TankPad = 0,
    Device = 1,
    Tank = 2,
}

export type ValueFilterType = {
    operator: string,
    value: number | undefined
}

export type TankRelationshipStateType = {

    activeTab: TankRelationshipTabEnum;

    filterType: FilterTypeEnum;

    capacityFilled: ValueFilterType | undefined;
    BOPD: ValueFilterType | undefined;
    BWPD: ValueFilterType | undefined;

    filterText: string;
    viewOptionState: ViewOptionStateEnum;
    downloadUid: string | undefined;

    //data: GridData | undefined;

    expanded: boolean;
};

let defaultTankRelationshipState: TankRelationshipStateType = {

    activeTab: TankRelationshipTabEnum.TankPad,
    filterType: FilterTypeEnum.TankPad | FilterTypeEnum.TankBattery | FilterTypeEnum.Tank | FilterTypeEnum.Device,
    filterText: '',
    capacityFilled:  undefined,
    BOPD: undefined,
    BWPD: undefined,

    viewOptionState: ViewOptionStateEnum.Default,
    downloadUid: undefined,
    //data: undefined,

    expanded: true
};

export {defaultTankRelationshipState}

enum TankRelationshipStateActionType {
    ActiveTabChanged = 'TANK_RELATIONSHIP_TAB_CHANGED',
    FilterTypeChanged = 'TANK_RELATIONSHIP_STARTDATE_CHANGED',
    FilterTextChanged = 'TANK_RELATIONSHIP_FILTER_TEXT_CHANGED',
    CapacityFilledChanged = 'TANK_RELATIONSHIP_CAPACITY_FILLED_CHANGED',
    BOPDChanged = 'TANK_RELATIONSHIP_BOPD_CHANGED',
    BWPDChanged = 'TANK_RELATIONSHIP_BWPD_CHANGED',
    DownloadUidChanged = 'TANK_RELATIONSHIP_DOWNLOAD_UID_CHANGED',
    //GridDataChanged = 'TANK_RELATIONSHIP_GRID_DATA_CHANGED',
    ExpandedChanged = 'TANK_RELATIONSHIP_EXPANDED_CHANGED',
    Reset = 'TANK_RELATIONSHIP_STATE_RESET'
}

type TankRelationshipStateActions = | {
    type: TankRelationshipStateActionType.ActiveTabChanged;
    payload: TankRelationshipTabEnum;
} | {
    type: TankRelationshipStateActionType.FilterTypeChanged;
    payload: FilterTypeEnum;
} | {
    type: TankRelationshipStateActionType.CapacityFilledChanged;
    payload: ValueFilterType;
} | {
    type: TankRelationshipStateActionType.BOPDChanged;
    payload: ValueFilterType;
} | {
    type: TankRelationshipStateActionType.BWPDChanged;
    payload: ValueFilterType;
} | {
    type: TankRelationshipStateActionType.FilterTextChanged;
    payload: string;
} | {
    type: TankRelationshipStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
//     type: TankRelationshipStateActionType.GridDataChanged;
//     payload: GridData | undefined;
// } | {
    type: TankRelationshipStateActionType.ExpandedChanged;
    payload: boolean;
} | {
    type: TankRelationshipStateActionType.Reset;
    payload: void;
};

const compareValueFilterType = (newState: ValueFilterType | undefined, defaultState: ValueFilterType | undefined) => newState?.operator !== defaultState?.operator || newState?.value !== defaultState?.value;

const isDirty = (newState: TankRelationshipStateType, 
                defaultState: TankRelationshipStateType) => 
                newState.filterText !== defaultState.filterText || 
                newState.filterType !== defaultState.filterType ||
                compareValueFilterType(newState.capacityFilled, defaultState.capacityFilled) ||
                compareValueFilterType(newState.BOPD, defaultState.BOPD) ||
                compareValueFilterType(newState.BWPD, defaultState.BWPD) ||
                newState.expanded !== defaultState.expanded;

function TankRelationshipStateReducer(state: TankRelationshipStateType, action: TankRelationshipStateActions): TankRelationshipStateType {

    const copy = {...state};
    switch (action.type) {

        case TankRelationshipStateActionType.ActiveTabChanged:
            copy.activeTab = action.payload;
            break;
        
        case TankRelationshipStateActionType.FilterTypeChanged:
            copy.filterType = action.payload;
            break;

        case TankRelationshipStateActionType.CapacityFilledChanged:
            copy.capacityFilled = action.payload;
            break;

        case TankRelationshipStateActionType.BOPDChanged:
            copy.BOPD = action.payload;
            break;

        case TankRelationshipStateActionType.BWPDChanged:
            copy.BWPD = action.payload;
            break;

        case TankRelationshipStateActionType.FilterTextChanged:
            copy.filterText = action.payload;
            break;
   
        case TankRelationshipStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            break;

        // case TankRelationshipStateActionType.GridDataChanged:
        //     copy.data = action.payload;
        //     break;

        case TankRelationshipStateActionType.ExpandedChanged:
            copy.expanded = action.payload;
            break;
    

        case TankRelationshipStateActionType.Reset:
            return {...defaultTankRelationshipState};
    
        default:
            return state;
    }

    copy.viewOptionState = isDirty(copy, defaultTankRelationshipState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;

    return copy;
}

export default function useTankRelationshipState() {

    const [state, dispatch] = useReducer(TankRelationshipStateReducer, {...defaultTankRelationshipState});

    const setActiveTab = useCallback((value: TankRelationshipTabEnum) => dispatch({
        type: TankRelationshipStateActionType.ActiveTabChanged,
        payload: value
    }), []);

    const setFilterType = useCallback((value: FilterTypeEnum) => dispatch({
        type: TankRelationshipStateActionType.FilterTypeChanged,
        payload: value
    }), []);

    const setFilterText = useCallback((value: string) => dispatch({
        type: TankRelationshipStateActionType.FilterTextChanged,
        payload: value
    }), []);


    const setCapacityFilter = useCallback((value: ValueFilterType) => dispatch({
        type: TankRelationshipStateActionType.CapacityFilledChanged,
        payload: value
    }), []);

    const setBOPDFilter = useCallback((value: ValueFilterType) => dispatch({
        type: TankRelationshipStateActionType.BOPDChanged,
        payload: value
    }), []);


    const setBWPDFilter = useCallback((value: ValueFilterType) => dispatch({
        type: TankRelationshipStateActionType.BWPDChanged,
        payload: value
    }), []);


    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: TankRelationshipStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    // const setGridData = useCallback((value: GridData | undefined) => dispatch({
    //     type: TankRelationshipStateActionType.GridDataChanged,
    //     payload: value
    // }), []);

    const setExpanded = useCallback((value: boolean) => dispatch({
        type: TankRelationshipStateActionType.ExpandedChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: TankRelationshipStateActionType.Reset,
        payload: undefined
    }), []);

    return {
        state,
        setActiveTab,
        setFilterType,
        setFilterText,
        setCapacityFilter,
        setBOPDFilter,
        setBWPDFilter,
        setDownloadUid,
        //setGridData,
        setExpanded,
        resetState
    };
}


